import type { FlatConfig } from 'domain/index';
import { getAvailableQuarters, getCurrentQuarter } from 'constants/quarterConfig';

export type Option = { value: string; label: string; short?: string };

export type SubscriptionAccess = { allQuarters: boolean; specialTerms: boolean };

// A vendor that declares its own terms sells exactly those, to every tier
// (SMC's term licensing). Vendors that do not keep the access-gated list.
const LEGACY_TERMS_SPECIAL = [12, 24, 36, 48, 60, 72, 84];
const LEGACY_TERMS_STANDARD = [36, 48, 60, 72, 84];

/** Pure option resolution for the Subscription section — no React, so the
 *  hook is left to orchestrate state and callbacks only. `access.allQuarters`
 *  is the caller's `ability.can(PERM.QUARTER_ALL)`; `access.specialTerms` is
 *  `ability.can(PERM.ADMIN_SKUS)` — callers translate from the ability seam,
 *  this module never imports `shared/access`. */
export function resolveSubscriptionOptions(
  caps: Record<string, any>,
  config: FlatConfig,
  access: SubscriptionAccess,
): { quarterOptions: Option[]; monthOptions: Option[]; serviceOptions: Option[] } {
  const quarterOptions: Option[] = (access.allQuarters ? getAvailableQuarters() : [getCurrentQuarter()])
    .map((q: string) => ({ value: q, label: q.replace('-', ' ') }));

  const declared: number[] | undefined = caps.subscriptionDurations as number[] | undefined;
  const months: number[] = declared ?? (access.specialTerms ? LEGACY_TERMS_SPECIAL : LEGACY_TERMS_STANDARD);
  const monthOptions: Option[] = months.map((m) => ({ value: String(m), label: `${m} months`, short: String(m) }));

  const services = (caps.serviceOptions ?? []) as Array<{ key: string; label: string; hidden?: boolean }>;
  // `hidden` options are no longer sold but stay in the registry so saved quotes
  // still resolve; offer one only when it is the quote's current selection (classic parity).
  const serviceOptions: Option[] = services
    .filter((s) => !s.hidden || s.key === config.serviceOption)
    .map((s) => ({ value: s.key, label: s.label }));

  return { quarterOptions, monthOptions, serviceOptions };
}
